import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { SupportApiService } from "../../Service/SupportApiService";
import Spinner from "../Common/Spinner";
import { CommonService } from "../../Service/CommonService";

interface Barrel {
  barrelId: string;
  barrelNumber: string;
  proof: number;
}

interface Lot {
  productName: string;
  lotNo: string;
  quantity: number;
  barrelNumbers: Barrel[];
}

interface ChildProps {
  customerServiceRequestId: string;
  reloadTable?: () => void;
  onClose: () => void;
}

const ManageBarrelProofingDetails: React.FC<ChildProps> = ({
  customerServiceRequestId,
  reloadTable,
  onClose,
}) => {
  const [show, setShow] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingData, setIsGettingData] = useState(false);
  const [lots, setLots] = useState<Lot[]>([]);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [editedProofs, setEditedProofs] = useState<Record<string, number>>({});
  const [editedValues, setEditedValues] = useState<Record<string, number>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [applyValue, setApplyValue] = useState<number | "">("");

  const handleClose = () => {
    setEditedValues({});
    onClose(); // call parent’s close handler
  };

  // Load data (API / mock)
  const fetchProofingDetails = async () => {
    setIsGettingData(true);

    try {
      const response = await SupportApiService.fetchBarrelProofDetails(
        customerServiceRequestId
      );

      setLots(response?.data);
      setSelectedLot(response?.data[0]); // default select first lot

      // Initialize editedProofs
      const initialProofs: Record<string, number> = {};
      response?.data.forEach((lot) => {
        lot.barrelNumbers.forEach((barrel) => {
          initialProofs[barrel.barrelId] = barrel.proof;
        });
      });
      setEditedProofs(initialProofs);
    } catch (error) {
      console.log(
        "Error occurred while getting barrel proofing details " + error
      );
    } finally {
      setIsGettingData(false);
    }
  };

  const handleProofValueChange = (detailId: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setEditedValues((prev) => ({
      ...prev,
      [detailId]: numericValue,
    }));
  };

  // On submit
  const handleSubmit = async () => {

  const errors: Record<string, string> = {};

  // Validate proof values
  lots.forEach((lot) => {
    lot.barrelNumbers.forEach((barrel) => {
      const proofValue = editedProofs[barrel.barrelId];
      if (!proofValue || proofValue <= 0) {
        errors[barrel.barrelId] = "Please enter valid Proof Value";
      }
    });
  });

  setValidationErrors(errors);

  // If there are validation errors, stop submitting
  if (Object.keys(errors).length > 0) 
    return;

    try {
      const updatedBarrels: {
        barrelId: string;
        barrelNumber: string;
        proof: number;
      }[] = [];

      lots.forEach((lot) => {
        lot.barrelNumbers.forEach((barrel) => {
          const newProof = editedProofs[barrel.barrelId];
          if (newProof !== undefined && newProof !== barrel.proof) {
            updatedBarrels.push({
              barrelId: barrel.barrelId,
              barrelNumber: barrel.barrelNumber,
              proof: newProof,
            });
          }
        });
      });

      if (updatedBarrels.length === 0) {
        CommonService.Toast.fire({
          title: "No changes detected in container proof",
          icon: "info",
        });
        return;
      }
      setIsLoading(true);

      await SupportApiService.updateBarrelProofingDetails(updatedBarrels);
      CommonService.Toast.fire({
        title: "Proof value updated successfully",
        icon: "success",
      });

      handleClose();
    } catch (error) {
      console.log("Error occurred while api calling " + error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle proof value change
  const handleProofChange = (barrelId: string, value: string) => {
    const numericValue = parseFloat(value);
    setEditedProofs((prev) => ({ ...prev, [barrelId]: numericValue }));

      // Remove validation error if user entered a valid number > 0
      setValidationErrors((prev) => {
        const updatedErrors = { ...prev };
        if (numericValue && numericValue > 0) {
          delete updatedErrors[barrelId];
        }
        return updatedErrors;
      });
  };

  const handleApplyToAll = () => {
    if (!applyValue || Number(applyValue) <= 0) {
      CommonService.Toast.fire({
        title: "Please Enter a Valid Proofing Value Before Applying.",
        icon: "warning",
      });
      return;
    }

    const updatedProofingValue = { ...editedProofs };
    const updatedErrors = { ...validationErrors };

    // Apply to all container across all lots
    lots.forEach((lot) => {
      lot.barrelNumbers.forEach((barrel) => {
        updatedProofingValue[barrel.barrelId] = Number(applyValue);
        // remove any existing error for this barrel
        if (updatedErrors[barrel.barrelId]) delete updatedErrors[barrel.barrelId];
      });
    });

    setEditedProofs(updatedProofingValue);
    setValidationErrors(updatedErrors);
    setApplyValue(""); // clear input after apply

    CommonService.Toast.fire({
      title: "Proofing Value Applied to All Containers",
      icon: "success",
    });
  };

  useEffect(() => {
    if (show) {
      fetchProofingDetails();
    }
  }, [show]);

  return (
      <Modal
        show={show}
        onHide={handleClose}
        className="modal__wd modal__wd--submitSellReq"
        size="lg"
      >
        <Modal.Header className="proofing-modal-header">
          <Modal.Title className="proofing-modal-title">
            Manage Proofing Details
          </Modal.Title>
        </Modal.Header>
  
        <Modal.Body className="proofing-modal-body">
          {isGettingData ? (
            <div className="text-center py-4">
              <Spinner />
              <p className="mt-2">Loading proofing details...</p>
            </div>
          ) : (
            <div className="proofing-modal-inner">
              <div className="row g-0">
                {/* Left Panel */}
                 <div className="col-md-5 col-lg-4">
                  <div className="list-wrapper">
                    {lots.map((lot) => (
                      <div
                        className={`list-item-wrapper`}
                        onClick={() => setSelectedLot(lot)}
                        style={{
                          backgroundColor:
                            selectedLot?.lotNo !== lot.lotNo
                              ? "#f0f0f0"
                              : "#fff",
                        }}
                      >
                        <div className="d-flex justify-content-between gap-1 w-100 align-items-center flex-wrap">
                          <div>
                            <p className="list-inventory-title">
                              {lot.productName}
                            </p>
                            <p className="list-lot-no">Lot ID : {lot.lotNo}</p>
                          </div>

                          <div className="text-end ms-auto text-nowrap">
                            <span className="own-barrels ms-2">{lot.barrelNumbers.length} {lot.barrelNumbers.length > 1 ? "Containers" : "Container"}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Panel */}
                <div className="col-md-7 col-lg-8">
                    <div className="lot-details flex-grow-1 ps-3">
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                          <input
                            type="number"
                            min="0"
                            placeholder="Enter Proofing Value"
                            value={applyValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              // prevent negative numbers or invalid input
                              if (value === "" || Number(value) >= 0) {
                                setApplyValue(value ? Number(value) : "");
                              }
                            }}
                            style={{minWidth:"221px", width:"unset"}}
                            className="proofing-input all-valution-input"
                          />
                          <button className="secondary-btn ms-auto" style={{minWidth:"190px"}} onClick={handleApplyToAll} >
                          Apply Proofing
                        </button>
                      </div>
                      {selectedLot ? (
                        <>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Container Id</th>
                                <th>Proof Value</th>
                              </tr>
                            </thead>
                              <tbody>
                              {selectedLot.barrelNumbers.map((barrel) => (
                                <tr key={barrel.barrelId}>
                                  <td>
                                    <p>{barrel.barrelNumber}
                                      </p>
                                    </td>
                                  <td>
                                  <div className="position-relative">
                                    <input
                                    
                                      type="number"
                                      value={editedProofs[barrel.barrelId] ?? ""}
                                      onChange={(e) =>
                                        handleProofChange(
                                          barrel.barrelId,
                                          e.target.value
                                        )
                                      }
                                      className="proofing-input"
                                      placeholder="Enter Proof"
                                    />
                                    {validationErrors[barrel.barrelId] && (
                                      <span className="error-msg">
                                        {validationErrors[barrel.barrelId]}
                                      </span>
                                    )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <div className="text-muted">
                          Select a lot to view container details
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          )}
        </Modal.Body>
  
        <Modal.Footer className="justify-content-center pt-0">
          <div className="btn-group row d-flex justify-content-between w-100 proofing-modal-footer">
            <div className="col-6">
              <button
                className="secondary-btn w-100"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
            <div className="col-6">
              <button
                className="primary-btn w-100"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading && <Spinner />} Add Details
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
};

export default ManageBarrelProofingDetails;
